import {
  IconArrowBarToUp,
  IconColumns3,
  IconLock,
  IconRestore,
  IconTrash,
} from "@tabler/icons-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface ColumnCustomizerDialogProps<Column extends string> {
  /** Every column in catalogue order; fixed columns are listed but locked. */
  allColumns: readonly Column[];
  fixedColumns: readonly Column[];
  /** Non-fixed columns restored by "恢复默认". */
  defaultColumns: readonly Column[];
  /** Currently applied non-fixed columns, in display order. */
  visibleColumns: readonly Column[];
  label: (column: Column) => string;
  description: string;
  onApply: (columns: readonly Column[]) => void;
  /** Trigger text; defaults to the shared "自定义列" label. */
  triggerLabel?: string;
  triggerClassName?: string;
  triggerIcon?: ReactNode;
}

/**
 * Paper's two-pane column customizer (catalogue on the left, ordered visible
 * columns on the right). The draft applies only on confirm.
 */
export function ColumnCustomizerDialog<Column extends string>({
  allColumns,
  fixedColumns,
  defaultColumns,
  visibleColumns,
  label,
  description,
  onApply,
  triggerLabel,
  triggerClassName,
  triggerIcon,
}: ColumnCustomizerDialogProps<Column>) {
  const { t } = useTranslation("campaign");
  const [open, setOpen] = useState(false);
  const [draftColumns, setDraftColumns] = useState<Column[]>([]);
  const visible = new Set(draftColumns);
  const isFixed = (column: Column) => fixedColumns.includes(column);

  const openCustomizer = () => {
    setDraftColumns([...visibleColumns]);
    setOpen(true);
  };

  const toggleColumn = (column: Column, checked: boolean) => {
    setDraftColumns((current) =>
      checked
        ? current.includes(column)
          ? current
          : [...current, column]
        : current.filter((candidate) => candidate !== column),
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="xs"
        className={cn("h-8 rounded-[8px] px-2.5 text-[11px]", triggerClassName)}
        onClick={openCustomizer}
      >
        {triggerIcon ?? <IconColumns3 data-icon="inline-start" />}
        {triggerLabel ?? t("list.customColumns.trigger")}
      </Button>
      <DialogPopup
        className="max-w-[960px]"
        closeProps={{ "aria-label": t("list.customColumns.close") }}
      >
        <DialogHeader className="border-b border-line px-6 py-5">
          <DialogTitle className="text-base">{t("list.customColumns.title")}</DialogTitle>
          <DialogDescription className="sr-only">{description}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 px-6 py-5">
          <div className="grid h-[min(560px,calc(100vh-230px))] min-h-[360px] grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] overflow-hidden rounded-[10px] border border-line bg-surface">
            <section className="flex min-h-0 flex-col border-r border-line">
              <div className="flex h-12 shrink-0 items-center border-b border-line bg-subtle px-4">
                <h3 className="text-xs font-semibold text-ink">
                  {t("list.customColumns.allColumns")}
                </h3>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-2 paper-scrollbar">
                {allColumns.map((column) => {
                  const fixed = isFixed(column);
                  return (
                    <div
                      key={column}
                      className={cn(
                        "flex min-h-10 items-center gap-3 rounded-[8px] px-3 text-xs text-ink transition-colors hover:bg-subtle",
                        fixed && "text-muted-foreground",
                      )}
                    >
                      <Checkbox
                        aria-label={label(column)}
                        checked={fixed || visible.has(column)}
                        disabled={fixed}
                        onCheckedChange={(checked) => toggleColumn(column, checked === true)}
                      />
                      <button
                        type="button"
                        className={cn("flex-1 text-left", fixed && "cursor-default")}
                        disabled={fixed}
                        onClick={() => toggleColumn(column, !visible.has(column))}
                      >
                        {label(column)}
                      </button>
                      {fixed ? <IconLock className="size-3.5 text-muted-foreground" /> : null}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="flex min-h-0 flex-col">
              <div className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-line bg-subtle px-4">
                <h3 className="text-xs font-semibold text-ink">
                  {t("list.customColumns.visibleColumns", {
                    count: fixedColumns.length + draftColumns.length,
                  })}
                </h3>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="xs"
                    className="text-[11px] text-brand"
                    onClick={() => setDraftColumns([...defaultColumns])}
                  >
                    <IconRestore data-icon="inline-start" />
                    {t("list.customColumns.restoreDefault")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    className="text-[11px] text-muted-foreground"
                    onClick={() => setDraftColumns([])}
                  >
                    {t("list.customColumns.clear")}
                  </Button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-3 paper-scrollbar">
                <ColumnGroup
                  title={t("list.customColumns.fixedColumns", { count: fixedColumns.length })}
                >
                  {fixedColumns.map((column) => (
                    <div
                      key={column}
                      className="flex h-10 items-center gap-3 rounded-[8px] px-3 text-xs text-ink"
                    >
                      <IconLock className="size-3.5 text-muted-foreground" />
                      <span className="flex-1">{label(column)}</span>
                    </div>
                  ))}
                </ColumnGroup>

                <ColumnGroup
                  title={t("list.customColumns.nonFixedColumns", { count: draftColumns.length })}
                  className="mt-3"
                >
                  {draftColumns.length > 0 ? (
                    draftColumns.map((column, index) => (
                      <div
                        key={column}
                        className="group flex h-10 items-center gap-3 rounded-[8px] px-3 text-xs text-ink hover:bg-subtle"
                      >
                        <span className="w-5 text-center font-mono text-[10px] text-muted-foreground">
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{label(column)}</span>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          disabled={index === 0}
                          aria-label={t("list.customColumns.moveToTopAria", {
                            column: label(column),
                          })}
                          title={t("list.customColumns.moveToTop")}
                          onClick={() =>
                            setDraftColumns((current) => [
                              column,
                              ...current.filter((candidate) => candidate !== column),
                            ])
                          }
                        >
                          <IconArrowBarToUp />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={t("list.customColumns.removeAria", {
                            column: label(column),
                          })}
                          title={t("list.customColumns.remove")}
                          onClick={() => toggleColumn(column, false)}
                        >
                          <IconTrash />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="flex min-h-24 items-center justify-center px-4 text-center text-xs text-muted-foreground">
                      {t("list.customColumns.empty")}
                    </div>
                  )}
                </ColumnGroup>
              </div>
            </section>
          </div>
        </div>

        <DialogFooter className="py-3.5">
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("list.customColumns.cancel")}
          </Button>
          <Button
            onClick={() => {
              onApply(draftColumns);
              setOpen(false);
            }}
          >
            {t("list.customColumns.confirm")}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

function ColumnGroup({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("overflow-hidden rounded-[9px] border border-line", className)}>
      <div className="flex h-9 items-center border-b border-line bg-subtle px-3 text-[11px] font-medium text-muted-foreground">
        {title}
      </div>
      <div className="py-1">{children}</div>
    </div>
  );
}
